import React from "react";
import {
  TbCamera,
  TbEditCircle,
  TbPlaylistAdd,
  TbPlaylistX,
  TbUserEdit,
} from "react-icons/tb";
import { useAuthContext } from "../../../context/authContext";
import { useEmployeeContext } from "../../../context/employee-context";
import {
  Button,
  Col,
  Divider,
  FabButton,
  Flex,
  Gap,
  InputField,
  Row,
  SelectField,
  Typography,
} from "../../../ppui";
import { subtractYearsFromDate } from "../../../utils/common";
import useCommonfn from "../../../utils/useCommonfn";
import { PhotoIcon, PhotoUpload } from "../../style";
import { EMPLOYEE_FORM_FIELDS, RELATION } from "./profile-constant";

const ProfileForm = ({
  employeeProfileInput,
  getInputFieldProps,
  fileInputRef,
  previewURL,
  openSnackbar,
  setEmployeeProfileInput,
  setPreviewURL,
  setSelectedFile,
  createFormUpdateLoading,
  SubmitWithNextBtn,
  SubmitAfterView,
  isOpenPresentAddForm,
  handleClickPresentAddress
}) => {
  const { navigateToDashBoard } = useCommonfn();
  const {
    stateList,
    branchList,
    departmentList,
    designationList,
    employmentTypeList,
    shiftManagement,
  } = useEmployeeContext() || {};
  const { orgDetails } = useAuthContext();
  
  const getRelation = (relation) => {
    switch (relation) {
      case "married":
        return RELATION.MARRIED;
      case "single":
        return RELATION.SINGLE;
      case "windowed":
        return RELATION.WIDOWED;
      case "divorced":
        return RELATION.DIVORCED;
      default:
        return [];
    }
  };
  const relation = employeeProfileInput.marital_status;
  const options = getRelation(relation);

  const orgEstablishmentDate = new Date(orgDetails.date_of_org);

  // Modified getInputFieldProps for join_date to include min date validation
  const getModifiedInputFieldProps = (fieldName) => {
    const baseProps = getInputFieldProps(fieldName);
    
    if (fieldName === EMPLOYEE_FORM_FIELDS.join_date.name) {
      return {
        ...baseProps,
        min: orgDetails?.date_of_org?.split('T')[0], // Set minimum date as org establishment date
        onChange: (e) => {
          const selectedDate = new Date(e.target.value);
          if (selectedDate < orgEstablishmentDate) {
            openSnackbar({
              message: "Join date cannot be earlier than organization's establishment date",
              color: "danger",
            });
            return;
          }
          // Call the original onChange handler
          baseProps.onChange(e);
        }
      };
    }
    
    return baseProps;
  };


  return (
    <>
      <Typography variant="h5">Basic Information</Typography>
      <Row mb={20} alignItem={"center"}>
        <Col sm={12} md={"auto"}>
          <PhotoUpload
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
          >
            {!previewURL && <TbUserEdit />}
            <input
              type="file"
              id="image"
              name="image"
              onChange={(event) => {
                const file = event.target.files[0];
                if (file) {
                  const maxSizeInBytes = 2 * 1024 * 1024;
                  if (file.size > maxSizeInBytes) {
                    openSnackbar({
                      message: "File size should not exceed 2 MB.",
                      color: "danger",
                    });
                    return;
                  }
                  const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

                  if (!allowedTypes.includes(file.type)) {
                    openSnackbar({
                      message: "Only JPG and PNG files are allowed.",
                      color: "danger",
                    });
                    return; // Stop if the file type is not allowed
                  }

                  setSelectedFile(file);
                  setPreviewURL(URL.createObjectURL(file)); // Create a preview URL

                  setEmployeeProfileInput((prevUser) => ({
                    ...prevUser,
                    logo: file,
                  }));
                }
              }}
              ref={fileInputRef}
              accept=".jpg, .jpeg, .png, image/jpeg, image/png"
              hidden
            />
            {previewURL && <img src={previewURL} alt="" />}
            <PhotoIcon>
              <FabButton
                size="xs"
                variant="contained"
                color="primary"
                icon={previewURL ? <TbEditCircle /> : <TbCamera />}
              />
            </PhotoIcon>
          </PhotoUpload>
        </Col>
        <Col col-xs={"auto"}>
          <Row rowGap>
            <Col sm={12} md={6} lg={6} xxl={4}>
              <InputField
                {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.first_name.name)}
              />
            </Col>
            <Col sm={12} md={6} lg={6} xxl={4}>
              <InputField
                {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.last_name.name)}
              />
            </Col>
            <Col sm={12} md={6} lg={6} xxl={4}>
              <InputField
                {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.phone.name)}
              />
            </Col>
            <Col sm={12} md={6} lg={6} xxl={4}>
              <InputField
                {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.email.name)}
              />
            </Col>
            <Col sm={12} md={6} lg={6} xxl={4}>
              <InputField
                {...getInputFieldProps(
                  EMPLOYEE_FORM_FIELDS.alternative_phone.name
                )}
              />
            </Col>
            <Col sm={12} md={6} lg={6} xxl={4}>
              <InputField
                {...getInputFieldProps(
                  EMPLOYEE_FORM_FIELDS.personal_email.name
                )}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider gap={20} />
      <Typography variant="h5">Employee Details</Typography>
      <Row rowGap mb={20}>
        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.employment_type.name)}
            options={employmentTypeList}
          />
        </Col>

        {/* {employeeProfileInput.employment_type === "full_time" && (
         <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
           <SelectField
             {...getInputFieldProps(
               EMPLOYEE_FORM_FIELDS.employment_status.name
             )}
           />
         </Col>
        )} */}

        {employeeProfileInput.employment_type === "probationary" && (
          <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
            <InputField
              {...getInputFieldProps(
                EMPLOYEE_FORM_FIELDS.provision_period_days.name
              )}
            />
          </Col>
        )}

        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
        <InputField
            {...getModifiedInputFieldProps(EMPLOYEE_FORM_FIELDS.join_date.name)}
            valueSinc
            onKeyDown={(e) => e.preventDefault()}
          />
        </Col>

        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.branch_id.name)}
            options={branchList}
          />
        </Col>

        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.department_id.name)}
            options={departmentList}
            onChange={(e) => {
              // Clear designation when department changes
              setEmployeeProfileInput((prev) => ({
                ...prev,
                department_id: e,
                designation_id: "", // Reset designation
              }));
            }}
          />
        </Col>

        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.designation_id.name)}
            options={designationList.filter(
              (designation) =>
                designation.dept_id === employeeProfileInput.department_id
            )}
          />
        </Col>
        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.status.name)}
            defaultValue={"onboarding"}
          />
        </Col>
        {shiftManagement && (
          <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
            <SelectField
              {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.shift_type.name)}
            />
          </Col>
        )}
        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
            <SelectField
              {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.salary_type.name)}
            />
          </Col>
        {employeeProfileInput.employment_type === "contractual" && (
          <>
            <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
              <InputField
                {...getInputFieldProps(
                  EMPLOYEE_FORM_FIELDS.contract_start.name
                )}
                valueSinc
                onKeyDown={(e) => e.preventDefault()}
                min={employeeProfileInput.join_date}
              />
            </Col>

            <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
              <InputField
                {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.contract_end.name)}
                valueSinc
                onKeyDown={(e) => e.preventDefault()}
                min={employeeProfileInput.contract_start}
              />
            </Col>
          </>
        )}
       
      </Row>

      <Divider gap={20} />
      <Typography variant="h5">Personal Information</Typography>
      <Row rowGap mb={20}>
        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <InputField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.date_of_birth.name)}
            valueSinc
            onKeyDown={(e) => e.preventDefault()}
            max={subtractYearsFromDate(employeeProfileInput.join_date, 18)}
          />
        </Col>
        {/* <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.qualification.name)}
          />
        </Col> */}
        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <InputField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.qualification.name)}
          />
        </Col>
        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.blood_group.name)}
          />
        </Col>

        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.gender.name)}
          />
        </Col>

        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.marital_status.name)}
          />
        </Col>

        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <InputField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.guardian_name.name)}
            // required={employeeProfileInput.guardian_name}
          />
        </Col>

        <Col sm={12} md={4} lg={4} xl={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(
              EMPLOYEE_FORM_FIELDS.guardian_relationship.name
            )}
            options={options}
            // required={employeeProfileInput.guardian_relationship}
          />
        </Col>
      </Row>
      <Divider gap={20} />
      <Typography variant="h5">Permanent Address</Typography>
      <Row rowGap>
        <Col sm={12} md={6} lg={4} xxl={3}>
          <InputField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.permanent_address.name)}
          />
        </Col>

        <Col sm={12} md={6} lg={4} xxl={3}>
          <InputField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.permanent_city.name)}
          />
        </Col>

        <Col sm={12} md={6} lg={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.permanent_state.name)}
            options={stateList}
          />
        </Col>

        <Col sm={12} md={6} lg={4} xxl={3}>
          <SelectField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.permanent_country.name)}
            value={"India"}
          />
        </Col>

        <Col sm={12} md={6} lg={4} xxl={3}>
          <InputField
            {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.permanent_pincode.name)}
          />
        </Col>

        <Col sm={12} md={6} lg={4} xxl={3}>
          <InputField
            {...getInputFieldProps(
              EMPLOYEE_FORM_FIELDS.permanent_landmark.name
            )}
          />
        </Col>
        {!isOpenPresentAddForm && (
          <Col sm={12} md={6} lg={4} xxl={3}>
            <Button
              onClick={()=>handleClickPresentAddress()}
              fullWidth
              icon={<TbPlaylistAdd />}
              color="primary"
            >
              Add Present Address
            </Button>
          </Col>
        )}
      </Row>
      {isOpenPresentAddForm && (
        <>
          <Divider gap={20} />
          <Typography variant="h5">Present Address</Typography>
          <Row rowGap>
            <Col sm={12} md={6} lg={4} xxl={3}>
              <InputField
                {...getInputFieldProps(
                  EMPLOYEE_FORM_FIELDS.present_address.name
                )}
              />
            </Col>
            <Col sm={12} md={6} lg={4} xxl={3}>
              <InputField
                {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.present_city.name)}
              />
            </Col>
            <Col sm={12} md={6} lg={4} xxl={3}>
              <SelectField
                {...getInputFieldProps(EMPLOYEE_FORM_FIELDS.present_state.name)}
                options={stateList}
              />
            </Col>
            <Col sm={12} md={6} lg={4} xxl={3}>
              <SelectField
                {...getInputFieldProps(
                  EMPLOYEE_FORM_FIELDS.present_country.name
                )}
                value={"India"}
              />
            </Col>
            <Col sm={12} md={6} lg={4} xxl={3}>
              <InputField
                {...getInputFieldProps(
                  EMPLOYEE_FORM_FIELDS.present_pincode.name
                )}
              />
            </Col>
            <Col sm={12} md={6} lg={4} xxl={3}>
              <InputField
                {...getInputFieldProps(
                  EMPLOYEE_FORM_FIELDS.present_landmark.name
                )}
              />
            </Col>
            <Col sm={12} md={6} lg={4} xxl={3}>
              <Button
                onClick={()=>handleClickPresentAddress()}
                fullWidth
                icon={<TbPlaylistX />}
                color="danger"
              >
                Remove Present Address
              </Button>
            </Col>
          </Row>
        </>
      )}

      <Gap height={100} />

      <Flex justifyContent="flex-end" gap={"10"}>
        <Button onClick={navigateToDashBoard} variant="outlined">
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={SubmitAfterView}
          loading={createFormUpdateLoading}
          variant="outlined"
        >
          Save & Exit
        </Button>
        <Button
          color="primary"
          onClick={SubmitWithNextBtn}
          loading={createFormUpdateLoading}
          variant="contained"
        >
          Next
        </Button>
      </Flex>
    </>
  );
};

export default ProfileForm;
